import {
  createReducer,
  createAction,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { UserState, Publisher, ApiUserProfile, Role } from '../types';
import { RootState, AppDispatch } from '.';
import { chicoryFetcher } from '../utils';
import { setFeatureFlags } from './featureFlags';
import { availablePubsAction, selectedPubsAction } from './publisherSlice';

type UserInstruction = {
  fetchedEmail: string;
  fetchedName?: string;
  fetchedPicture?: string;
  hasPremium?: boolean;
  role?: Role;
  publishers: Publisher[];
  paypalEmail?: string;
  status: string;
};

export const logoutAction = createAction('LOGOUT');

export const login = createAsyncThunk<
  UserInstruction, // Return type
  void, // argument to payload creator
  { state: RootState; dispatch: AppDispatch }
>('LOGIN', async (_, { getState, dispatch }) => {
  const data: ApiUserProfile = await chicoryFetcher('/auth/profile').then(
    (response) => response.json()
  );
  dispatch(setFeatureFlags({ showImpressions: data.showImpressions }));

  const state = getState();

  const user: UserInstruction = {
    fetchedEmail: data.email,
    fetchedName: data.name,
    fetchedPicture: data.picture,
    paypalEmail: data.paypalEmail,
    role: data.role,
    publishers: data.publishers,
    status: data.status,
  };

  // Ensure pubs in url are valid, remove if not
  const availablePubIds = user.publishers.map((pub) => pub.id);
  const validSelectedPubs = state.pubs.selectedPubs.filter((selectedPubId) =>
    availablePubIds.includes(selectedPubId)
  );

  dispatch(availablePubsAction({ availablePubs: user.publishers }));

  if (state.pubs.selectedPubs.length !== validSelectedPubs.length) {
    dispatch(selectedPubsAction({ selectedPubs: validSelectedPubs }));
  }

  return user;
});

// Define the reducer functions using createReducer
const initialUser: null = null;
export const userReducer = createReducer<UserState>(initialUser, (builder) => {
  builder.addCase(login.fulfilled, (_, action) => {
    return {
      email: action.payload.fetchedEmail,
      paypalEmail: action.payload.paypalEmail,
      name: action.payload.fetchedName,
      picture: action.payload.fetchedPicture,
      role: action.payload.role,
      publishers: action.payload.publishers,
      hasPremium: action.payload.publishers.some(
        (pub) => pub.isPremium === true
      ),
      status: action.payload.status,
    };
  });
  builder.addCase(login.rejected, (_, { error }) => {
    console.error('login failed', error);
  });
  builder.addCase(logoutAction, () => {
    return null;
  });
});
